import React, { Fragment, useCallback, useState } from "react";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { RiFileExcel2Line, RiFilePdf2Line } from "react-icons/ri";
import { FaArrowLeft, FaArrowRight, FaCheckCircle, FaChevronDown, FaRegClock } from "react-icons/fa";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { GetSelectedLanguage } from "../../helper/Method";
import { NoResult } from "../../helper/Components";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { applyClassToCharacters } from "../../../constants/const";
import { ApiNisabReport } from "./ApiNisabReport";
import SearchRafiqWiseNisabReport from "./SearchRafiqWiseNisabReport";
import "./NisabReport.css";

const PAGE_SIZE = 25;

const BookPills = ({ books, type }) =>
  books.length === 0 ? (
    <span className="text-muted">-</span>
  ) : (
    books.map((b, i) => (
      <span key={b.id} className={`nisab-pill ${type} ur`} style={{ animationDelay: `${Math.min(i, 20) * 25}ms` }}>
        {b.name}
      </span>
    ))
  );

// 1 … 4 5 6 … 12 style window around the current page
const pageWindow = (page, total) => {
  const pages = new Set([1, total, page - 1, page, page + 1]);
  const sorted = [...pages].filter((n) => n >= 1 && n <= total).sort((x, y) => x - y);
  const out = [];
  sorted.forEach((n, i) => {
    if (i > 0 && n - sorted[i - 1] > 1) out.push("…");
    out.push(n);
  });
  return out;
};

// leaf unit first, its parents underneath as a muted trail
const UnitCell = ({ path }) => {
  const parts = (path || "").split(" / ");
  const leaf = parts[parts.length - 1];
  const parents = parts.slice(0, -1).reverse();
  return (
    <div>
      <div className="nisab-unit-leaf">{applyClassToCharacters(leaf, "ur")}</div>
      {parents.length > 0 && (
        <div className="nisab-unit-path">
          {parents.map((name, i) => (
            <span key={`${name}${i}`}>
              {i > 0 && <span className="nisab-unit-sep">‹</span>}
              {applyClassToCharacters(name, "ur")}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const Skeleton = () => (
  <div>
    {[0, 1, 2, 3, 4].map((i) => (
      <div key={i} className="nisab-skeleton" style={{ animationDelay: `${i * 80}ms` }} />
    ))}
  </div>
);

const RafiqWiseNisabReport = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const isUrdu = GetSelectedLanguage() !== "en";
  const loggedInUnitId = encryptStorage.getItem("loggedInUnitId");
  const [filter, setFilter] = useState({
    unit: { value: loggedInUnitId, lable: "" },
    isSubUnits: true,
    syllabi: [],
    status: "all",
    search: "",
  });
  const [report, setReport] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const buildRequest = (pageNumber) => ({
    unitId: filter.unit.value,
    isSubUnits: filter.isSubUnits,
    syllabusIds: filter.syllabi.map((s) => s.value),
    search: filter.search,
    status: filter.status,
    pageNumber,
    pageSize: PAGE_SIZE,
  });

  const load = useCallback(
    (pageNumber) => {
      setLoading(true);
      setExpanded(null);
      ApiNisabReport()
        .GetReport(buildRequest(pageNumber))
        .then((res) => {
          setReport(res.data);
          setPage(pageNumber);
        })
        .catch((err) => console.log(err))
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter]
  );

  const toggleDetail = (row) => {
    if (expanded === row.memberId) return setExpanded(null);
    setExpanded(row.memberId);
    setDetail(null);
    ApiNisabReport()
      .GetDetail({ memberId: row.memberId, syllabusIds: filter.syllabi.map((s) => s.value) })
      .then((res) => setDetail(res.data))
      .catch((err) => console.log(err));
  };

  const download = (kind) => {
    const api = ApiNisabReport();
    const isExcel = kind === "excel";
    setDownloading(kind);
    toast.info(t("nisab_report_generating"));
    (isExcel ? api.GetExcel : api.GetPdf)(buildRequest(1))
      .then((res) => {
        const type = isExcel
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf";
        saveAs(new Blob([res.data], { type }), `NisabReport.${isExcel ? "xlsx" : "pdf"}`);
      })
      .catch((err) => {
        console.log(err);
        toast.error(t("nisab_report_download_failed"));
      })
      .finally(() => setDownloading(null));
  };

  const totalPages = report ? Math.max(1, Math.ceil(report.totalRecords / PAGE_SIZE)) : 1;

  return (
    <div className="wrapper-custom wrapper wrapper-content">
      <div className="nisab-hero">
        <button type="button" className="nisab-back" onClick={() => history.goBack()} aria-label="back">
          {isUrdu ? <FaArrowRight /> : <FaArrowLeft />}
        </button>
        <h2 className={isUrdu ? "ur" : ""}>{t("nisab_report_title")}</h2>
        <p className={isUrdu ? "ur" : ""}>{t("nisab_report_subtitle")}</p>
      </div>

      <div className="nisab-card" style={{ animationDelay: "80ms" }}>
        <SearchRafiqWiseNisabReport filter={filter} setFilter={setFilter} onSearch={() => load(1)} />
      </div>

      {loading && (
        <div className="nisab-card">
          <Skeleton />
        </div>
      )}

      {!loading && report && (
        <div className="nisab-card">
          <div className="nisab-card-head">
            <span />
            <div className="nisab-actions">
              <button type="button" className="nisab-btn-ghost excel" disabled={!!downloading} onClick={() => download("excel")}>
                {downloading === "excel" ? <span className="nisab-spin" /> : <RiFileExcel2Line size={18} />} Excel
              </button>
              <button type="button" className="nisab-btn-ghost pdf" disabled={!!downloading} onClick={() => download("pdf")}>
                {downloading === "pdf" ? <span className="nisab-spin" /> : <RiFilePdf2Line size={18} />} PDF
              </button>
            </div>
          </div>

          {report.rows.length === 0 ? (
            <NoResult />
          ) : (
            <>
              <div className="table-responsive">
                <table className="nisab-table">
                  <thead>
                    <tr>
                      <th style={{ width: 44 }}>#</th>
                      <th className={isUrdu ? "ur" : ""}>{t("nisab_report_reg_no")}</th>
                      <th className={isUrdu ? "ur" : ""}>{t("nisab_report_name")}</th>
                      <th className={isUrdu ? "ur" : ""}>{t("nisab_report_unit")}</th>
                      {report.syllabi.map((s) => (
                        <th key={s.value} className="text-center ur">
                          {s.label}
                        </th>
                      ))}
                      <th style={{ width: 40 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {report.rows.map((row, i) => {
                      const delay = { animationDelay: `${Math.min(i, 15) * 40}ms` };
                      const open = expanded === row.memberId;
                      return (
                        <Fragment key={row.memberId}>
                          <tr className={`nisab-row ${open ? "open" : ""}`} onClick={() => toggleDetail(row)}>
                            <td style={delay}>{(page - 1) * PAGE_SIZE + i + 1}</td>
                            <td style={delay}>{row.registrationNumber}</td>
                            <td style={delay}>
                              <strong>{applyClassToCharacters(row.memberName, "ur")}</strong>
                            </td>
                            <td style={delay}>
                              <UnitCell path={row.unitName} />
                            </td>
                            {row.syllabi.map((s) => (
                              <td key={s.syllabusId} style={delay} className="text-center">
                                <span className={`nisab-status ${s.isCompleted ? "done" : "todo"} ${isUrdu ? "ur" : ""}`}>
                                  {s.isCompleted ? <FaCheckCircle /> : <FaRegClock />}
                                  {t(s.isCompleted ? "nisab_report_status_completed" : "nisab_report_status_not_completed")}
                                </span>
                              </td>
                            ))}
                            <td style={delay}>
                              <FaChevronDown className="nisab-chevron" />
                            </td>
                          </tr>
                          {open && (
                            <tr>
                              <td colSpan={5 + report.syllabi.length}>
                                <div className="nisab-detail">
                                  {!detail ? (
                                    <Skeleton />
                                  ) : (
                                    detail.syllabi.map((s) => (
                                      <div key={s.syllabusId} className={`nisab-detail-block ${s.isCompleted ? "complete" : ""}`}>
                                        <div className="nisab-detail-head">
                                          <h5 className="ur">{s.syllabusName}</h5>
                                          <span className={`nisab-count ${s.isCompleted ? "done" : "todo"}`} dir="ltr">
                                            {s.completed.length} / {s.completed.length + s.remaining.length}
                                          </span>
                                        </div>
                                        <span className="nisab-detail-label">{t("nisab_report_remaining_books")}</span>
                                        <BookPills books={s.remaining} type="remaining" />
                                        <span className="nisab-detail-label">{t("nisab_report_completed_books")}</span>
                                        <BookPills books={s.completed} type="completed" />
                                      </div>
                                    ))
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="nisab-pager">
                <button type="button" className="nisab-page-btn" disabled={page <= 1} onClick={() => load(page - 1)}>
                  {t("nisab_report_prev")}
                </button>
                {pageWindow(page, totalPages).map((n, i) =>
                  n === "…" ? (
                    <span key={`gap${i}`} className="nisab-page-gap">
                      …
                    </span>
                  ) : (
                    <button
                      key={n}
                      type="button"
                      className={`nisab-page-num ${n === page ? "active" : ""}`}
                      onClick={() => n !== page && load(n)}
                    >
                      {n}
                    </button>
                  )
                )}
                <button type="button" className="nisab-page-btn" disabled={page >= totalPages} onClick={() => load(page + 1)}>
                  {t("nisab_report_next")}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default RafiqWiseNisabReport;
